package com.snakesworld.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Slider
import androidx.compose.material3.SliderDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.audio.SoundManager
import com.snakesworld.game.network.AuthState
import com.snakesworld.game.legal.PrivacyPolicy
import com.snakesworld.game.legal.SupportContact
import com.snakesworld.game.network.MultiplayerRepository
import com.snakesworld.game.ui.theme.JoystickType
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.LocalJoystickType
import com.snakesworld.game.ui.theme.appFontFamily
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.audio_mute
import snakesworld.shared.generated.resources.audio_sfx_title
import snakesworld.shared.generated.resources.audio_volume
import snakesworld.shared.generated.resources.cancel
import snakesworld.shared.generated.resources.clear_data_button
import snakesworld.shared.generated.resources.clear_data_card_title
import snakesworld.shared.generated.resources.clear_data_confirm
import snakesworld.shared.generated.resources.clear_data_desc
import snakesworld.shared.generated.resources.clear_data_dialog_desc
import snakesworld.shared.generated.resources.clear_data_dialog_title
import snakesworld.shared.generated.resources.clear_data_success
import snakesworld.shared.generated.resources.control_type_dpad
import snakesworld.shared.generated.resources.control_type_joystick
import snakesworld.shared.generated.resources.control_type_label
import snakesworld.shared.generated.resources.controls_guide_dpad
import snakesworld.shared.generated.resources.controls_guide_keyboard
import snakesworld.shared.generated.resources.controls_guide_title
import snakesworld.shared.generated.resources.settings
import snakesworld.shared.generated.resources.contact_email_label
import snakesworld.shared.generated.resources.privacy_open_button
import snakesworld.shared.generated.resources.support_card_desc
import snakesworld.shared.generated.resources.support_card_title
import snakesworld.shared.generated.resources.support_open_button
import snakesworld.shared.generated.resources.privacy_card_desc
import snakesworld.shared.generated.resources.privacy_card_title

@Composable
fun SettingsScreen(
    soundManager: SoundManager,
    repository: MultiplayerRepository,
    onUserDataCleared: () -> Unit = {},
    onBackToMenu: () -> Unit,
) {
    val canvasColors = LocalGameCanvasColors.current
    val uriHandler = LocalUriHandler.current
    val joystickTypeState = LocalJoystickType.current
    val authState by repository.authState.collectAsStateWithLifecycle()
    val coroutineScope = rememberCoroutineScope()
    var volume by remember { mutableFloatStateOf(soundManager.getVolume()) }
    var isMuted by remember { mutableStateOf(false) }
    var showClearDialog by remember { mutableStateOf(false) }
    var isClearingData by remember { mutableStateOf(false) }
    var clearDataSuccess by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.backgroundColor)
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(16.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(top = 44.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Header
            Text(
                text = "⚙ ${stringResource(Res.string.settings)}",
                color = canvasColors.hudTextColor,
                fontSize = 24.sp,
                fontWeight = FontWeight.Black,
                fontFamily = appFontFamily(),
                letterSpacing = 2.sp
            )

            // Joystick / Control Type Selection
            SettingsCard(title = stringResource(Res.string.control_type_label), canvasColors = canvasColors) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    ControlOptionButton(
                        text = stringResource(Res.string.control_type_dpad),
                        isSelected = joystickTypeState.value == JoystickType.Pad,
                        onClick = { joystickTypeState.value = JoystickType.Pad },
                        canvasColors = canvasColors,
                        modifier = Modifier.weight(1f)
                    )
                    ControlOptionButton(
                        text = stringResource(Res.string.control_type_joystick),
                        isSelected = joystickTypeState.value == JoystickType.Joystick,
                        onClick = { joystickTypeState.value = JoystickType.Joystick },
                        canvasColors = canvasColors,
                        modifier = Modifier.weight(1f)
                    )
                }
            }

            // Audio & SFX Controls
            SettingsCard(title = stringResource(Res.string.audio_sfx_title), canvasColors = canvasColors) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = stringResource(Res.string.audio_volume),
                            color = canvasColors.hudTextColor,
                            fontSize = 12.sp,
                            fontFamily = appFontFamily()
                        )
                        Text(
                            text = "${(volume * 100).toInt()}%",
                            color = canvasColors.accentColor,
                            fontSize = 12.sp,
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold
                        )
                    }

                    Slider(
                        value = volume,
                        onValueChange = {
                            volume = it
                            soundManager.setVolume(it)
                        },
                        colors = SliderDefaults.colors(
                            thumbColor = canvasColors.hudTextColor,
                            activeTrackColor = canvasColors.hudTextColor
                        )
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Checkbox(
                            checked = isMuted,
                            onCheckedChange = {
                                isMuted = it
                                soundManager.setMuted(it)
                            },
                            colors = CheckboxDefaults.colors(checkedColor = canvasColors.dangerColor)
                        )
                        Text(
                            text = stringResource(Res.string.audio_mute),
                            color = if (isMuted) canvasColors.dangerColor else canvasColors.hudTextColor,
                            fontSize = 12.sp,
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            // Controls Reference Guide
            SettingsCard(title = stringResource(Res.string.controls_guide_title), canvasColors = canvasColors) {
                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    Text(
                        text = stringResource(Res.string.controls_guide_dpad),
                        color = canvasColors.accentColor,
                        fontSize = 11.sp,
                        fontFamily = appFontFamily()
                    )
                    Text(
                        text = stringResource(Res.string.controls_guide_keyboard),
                        color = canvasColors.boundaryColor,
                        fontSize = 11.sp,
                        fontFamily = appFontFamily()
                    )
                }
            }


            // Privacy notice (Play Store / App Store)
            SettingsCard(title = stringResource(Res.string.privacy_card_title), canvasColors = canvasColors) {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text(
                        text = stringResource(Res.string.privacy_card_desc),
                        color = canvasColors.hudTextColor.copy(alpha = 0.8f),
                        fontSize = 11.sp,
                        fontFamily = appFontFamily()
                    )
                    Button(
                        onClick = { uriHandler.openUri(PrivacyPolicy.URL) },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = canvasColors.hudBgColor,
                            contentColor = canvasColors.hudTextColor,
                        ),
                        shape = RoundedCornerShape(6.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(42.dp)
                            .border(1.dp, canvasColors.hudTextColor, RoundedCornerShape(6.dp))
                    ) {
                        Text(
                            text = stringResource(Res.string.privacy_open_button),
                            color = canvasColors.hudTextColor,
                            fontWeight = FontWeight.Black,
                            fontFamily = appFontFamily(),
                            fontSize = 12.sp,
                            letterSpacing = 1.sp
                        )
                    }
                }
            }

            SettingsCard(title = stringResource(Res.string.support_card_title), canvasColors = canvasColors) {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text(
                        text = stringResource(Res.string.support_card_desc),
                        color = canvasColors.hudTextColor.copy(alpha = 0.8f),
                        fontSize = 11.sp,
                        fontFamily = appFontFamily()
                    )
                    Text(
                        text = stringResource(Res.string.contact_email_label),
                        color = canvasColors.hudTextColor.copy(alpha = 0.6f),
                        fontSize = 10.sp,
                        fontFamily = appFontFamily(),
                        fontWeight = FontWeight.Bold,
                    )
                    Text(
                        text = SupportContact.EMAIL,
                        color = canvasColors.accentColor,
                        fontSize = 13.sp,
                        fontFamily = appFontFamily(),
                        fontWeight = FontWeight.Bold,
                    )
                    Button(
                        onClick = { uriHandler.openUri(SupportContact.URL) },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = canvasColors.hudBgColor,
                            contentColor = canvasColors.hudTextColor,
                        ),
                        shape = RoundedCornerShape(6.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(42.dp)
                            .border(1.dp, canvasColors.hudTextColor, RoundedCornerShape(6.dp))
                    ) {
                        Text(
                            text = stringResource(Res.string.support_open_button),
                            color = canvasColors.hudTextColor,
                            fontWeight = FontWeight.Black,
                            fontFamily = appFontFamily(),
                            fontSize = 12.sp,
                            letterSpacing = 1.sp
                        )
                    }
                }
            }

            // Data Management / Clear User Data Card
            SettingsCard(title = stringResource(Res.string.clear_data_card_title), canvasColors = canvasColors) {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text(
                        text = stringResource(Res.string.clear_data_desc),
                        color = canvasColors.hudTextColor.copy(alpha = 0.8f),
                        fontSize = 11.sp,
                        fontFamily = appFontFamily()
                    )

                    Button(
                        onClick = {
                            clearDataSuccess = false
                            showClearDialog = true
                        },
                        enabled = !isClearingData,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = canvasColors.dangerColor,
                            contentColor = Color.White,
                            disabledContainerColor = canvasColors.dangerColor.copy(alpha = 0.4f),
                            disabledContentColor = Color.White.copy(alpha = 0.6f)
                        ),
                        shape = RoundedCornerShape(6.dp),
                        modifier = Modifier.fillMaxWidth().height(42.dp)
                    ) {
                        if (isClearingData) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                color = Color.White,
                                strokeWidth = 2.dp
                            )
                        } else {
                            Text(
                                text = "⚠ ${stringResource(Res.string.clear_data_button)}",
                                color = Color.White,
                                fontWeight = FontWeight.Black,
                                fontFamily = appFontFamily(),
                                fontSize = 12.sp,
                                letterSpacing = 1.sp
                            )
                        }
                    }

                    if (clearDataSuccess) {
                        Text(
                            text = "✔ ${stringResource(Res.string.clear_data_success)}",
                            color = canvasColors.accentColor,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            fontFamily = appFontFamily()
                        )
                    }
                }
            }

        }

        ScreenBackButton(
            onBack = onBackToMenu,
            enabled = !isClearingData,
            modifier = Modifier.align(Alignment.TopStart),
            canvasColors = canvasColors,
        )

        // Confirmation Dialog to Erase All User Data with Animated Enter/Exit
        ArcadeDialog(
            visible = showClearDialog,
            onDismissRequest = { if (!isClearingData) showClearDialog = false },
            title = {
                Text(
                    text = "⚠ ${stringResource(Res.string.clear_data_dialog_title)}",
                    fontFamily = appFontFamily(),
                    fontWeight = FontWeight.Black,
                    color = canvasColors.dangerColor,
                    fontSize = 16.sp
                )
            },
            text = {
                Text(
                    text = stringResource(Res.string.clear_data_dialog_desc),
                    fontFamily = appFontFamily(),
                    fontSize = 13.sp,
                    color = canvasColors.hudTextColor
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        coroutineScope.launch {
                            isClearingData = true
                            try {
                                val uid = (authState as? AuthState.Authenticated)?.uid.orEmpty()
                                val result = repository.clearUserData(uid)
                                // Always dismiss when the erase attempt finishes (success or failure).
                                showClearDialog = false
                                isClearingData = false
                                if (result.isSuccess) {
                                    clearDataSuccess = true
                                    onUserDataCleared()
                                }
                            } catch (_: Throwable) {
                                showClearDialog = false
                                isClearingData = false
                            }
                        }
                    },
                    enabled = !isClearingData,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = canvasColors.dangerColor,
                        contentColor = Color.White
                    ),
                    shape = RoundedCornerShape(6.dp)
                ) {
                    if (isClearingData) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = Color.White,
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(
                            text = stringResource(Res.string.clear_data_confirm),
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showClearDialog = false },
                    enabled = !isClearingData,
                    colors = ButtonDefaults.textButtonColors(contentColor = canvasColors.hudTextColor)
                ) {
                    Text(
                        text = stringResource(Res.string.cancel),
                        fontFamily = appFontFamily(),
                        color = canvasColors.hudTextColor
                    )
                }
            },
            borderColor = canvasColors.dangerColor,
            canvasColors = canvasColors
        )
    }
}

@Composable
private fun SettingsCard(
    title: String,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(canvasColors.hudBgColor, RoundedCornerShape(8.dp))
            .border(1.dp, canvasColors.gridColor, RoundedCornerShape(8.dp))
            .padding(14.dp)
    ) {
        Text(
            text = title,
            color = canvasColors.hudTextColor,
            fontSize = 13.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = appFontFamily(),
            letterSpacing = 1.sp
        )
        Spacer(modifier = Modifier.height(10.dp))
        content()
    }
}

@Composable
private fun ControlOptionButton(
    text: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
    modifier: Modifier = Modifier,
) {
    val shape = RoundedCornerShape(4.dp)
    val interactionSource = remember { MutableInteractionSource() }
    Box(
        modifier = modifier
            .clip(shape)
            .background(
                color = if (isSelected) canvasColors.hudTextColor else Color.Transparent,
                shape = shape
            )
            .border(
                width = 1.dp,
                color = if (isSelected) canvasColors.hudTextColor else canvasColors.gridColor,
                shape = shape
            )
            .clickable(
                interactionSource = interactionSource,
                indication = ripple(
                    bounded = true,
                    color = if (isSelected) Color.Black else canvasColors.hudTextColor
                ),
                onClick = onClick
            )
            .padding(vertical = 8.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = text,
            color = if (isSelected) Color.Black else canvasColors.hudTextColor,
            fontSize = 11.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = appFontFamily()
        )
    }
}
